// @ts-nocheck
/*
 * User Profile — rewritten to obd3-gui idiom (shadcn Card sections +
 * shadcn Dialogs). All v1 state, effects, API calls, toasts, role gates,
 * logout flow, panel-customization saves, and modal contents are preserved
 * verbatim. The Bootstrap chrome (Card/Row/Col/Table/Modal/Badge) is gone.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { trackAppLogout } from '@/service/activityTracker';
import {
  ArrowLeft,
  LogOut,
  RefreshCw,
  Info,
  Loader2,
  Columns3,
  Type as TypeIcon,
} from 'lucide-react';
import {
  FiUser,
  FiMail,
  FiShield,
  FiCalendar,
  FiCreditCard,
  FiBriefcase,
  FiUsers,
  FiMapPin,
  FiLayers,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiHome,
  FiPhone,
  FiMap,
  FiKey,
  FiActivity,
  FiType,
  FiHeart,
} from 'react-icons/fi';
import { MdFontDownload } from 'react-icons/md';

import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_USERS_PROFILE,
  API_USER_NUMBER_VOICE_PLANS,
  API_USER_LOGOUT,
  API_COMPANY_PROFILE,
  API_CALL_CENTRE_PROFILE,
  API_TTS_DEMO_QUOTA,
  API_COMPANY_DEMO_SUMMARY,
} from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser, logoutUser, switchBackUser, Storage } from '@/utilities/setting';
import { clearUser } from '@/store/setting/actions';
import {
  LICENCE_LABEL,
  USER_ROLES_MAP,
  USER_ROLES,
  ROUTES,
  INTERNAL_HOSTS,
  MODULE_ACL,
} from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import Loader from '@/components/partials/components/Loader';
import SidebarPositionToggle from '@/components/partials/dashboard/SidebarStyle/SidebarPositionToggle';
import socket from '@/utilities/socket';
import adminSocket from '@/utilities/adminSocket';
import { savePanelCustomization } from '@/utilities/panelCustomization';
import { getVoicePlanLabel, flattenPlansToArray } from '@/utilities/voicePlanUtils';
import { isExitBlocked, getExitBlockReason } from '@/router/zustand/callStore';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import AssetsVisibilityModal from '@/features/companies/components/AssetsVisibilityModal';
import { useAssetsVisibilityEligibility } from '@/hooks/useAssetsVisibilityEligibility';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { getAclModuleOptionLabels } from '@/utilities/aclOptionSummary';

/* ────────────────────────── shared helpers ────────────────────────── */
const parseDemoSnapshot = (snapshot) => {
  try {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const snapTalkTime = (secs) => {
  const s = Number(secs) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m ${s % 60}s`;
};

const snapDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN');
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/* ── Small meta-grid: matches UserConsole's bordered-pill layout ── */
const MetaTile = ({ icon: Icon, label, children }) => (
  <div className="rounded-md border border-border bg-muted/30 p-3">
    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {Icon ? <Icon size={12} className="opacity-70" /> : null}
      <span>{label}</span>
    </div>
    <div className="break-words text-sm">{children}</div>
  </div>
);

const MetaGrid = ({ children, cols = 2 }) => (
  <div
    className={
      'grid gap-3 ' +
      (cols === 3
        ? 'md:grid-cols-2 lg:grid-cols-3'
        : cols === 4
          ? 'sm:grid-cols-2 lg:grid-cols-4'
          : 'md:grid-cols-2')
    }
  >
    {children}
  </div>
);

const SectionCard = ({ title, action, children }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
      <CardTitle className="text-base font-semibold">{title}</CardTitle>
      {action}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

/* ────────────────────── demo snapshot subview ────────────────────── */
const SNAP_PANEL = 'rounded-md border border-border bg-muted/40 p-3';
const SnapStat = ({ label, value, accent }) => (
  <div
    className={SNAP_PANEL + ' border-l-[3px]'}
    style={{ borderLeftColor: accent || 'hsl(var(--border))' }}
  >
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className="text-sm font-bold">{value ?? '—'}</div>
  </div>
);

const SnapSection = ({ title, children }) => (
  <div className="mb-3">
    <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      {title}
    </div>
    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))' }}>
      {children}
    </div>
  </div>
);

const DemoSnapshotView = ({ snapshot }) => {
  const [showRaw, setShowRaw] = useState(false);
  const data = parseDemoSnapshot(snapshot);

  if (!data) {
    return (
      <pre className={SNAP_PANEL + ' mt-2 mb-0 max-h-[260px] overflow-auto text-xs'}>
        {String(snapshot)}
      </pre>
    );
  }

  const { overview = {}, calls = {}, credits = {}, entities = {}, range = {} } = data;
  const daily = Array.isArray(data.dailyCalls) ? data.dailyCalls : [];

  return (
    <div className="mt-2">
      {(range.startDate || range.endDate) && (
        <div className="mb-2.5 text-xs text-muted-foreground">
          Captured for {snapDate(range.startDate)} → {snapDate(range.endDate)}
        </div>
      )}

      <SnapSection title="Overview">
        <SnapStat label="Account Age" value={`${overview.account_age_days ?? 0} days`} accent="#6366f1" />
        <SnapStat label="Days Used" value={`${overview.days_used ?? 0} days`} accent="#0ea5e9" />
        <SnapStat label="Recharges" value={overview.recharge_count ?? 0} accent="#a855f7" />
        <SnapStat
          label="Converted"
          value={overview.converted ? snapDate(overview.conversion_date) : 'No'}
          accent="#f97316"
        />
        {overview.expiry_date ? (
          <SnapStat label="Expiry" value={snapDate(overview.expiry_date)} accent="#ef4444" />
        ) : null}
      </SnapSection>

      <SnapSection title="Calls">
        <SnapStat label="Total" value={calls.total ?? 0} accent="#14b8a6" />
        <SnapStat label="Incoming" value={calls.incoming ?? 0} accent="#0ea5e9" />
        <SnapStat label="Outgoing" value={calls.outgoing ?? 0} accent="#f97316" />
        <SnapStat label="Answered" value={calls.answered ?? 0} accent="#10b981" />
        <SnapStat label="Missed" value={calls.missed ?? 0} accent="#ef4444" />
        <SnapStat label="Talk Time" value={snapTalkTime(calls.total_talk_time_seconds)} accent="#22c55e" />
      </SnapSection>

      <SnapSection title="Voice Credits">
        <SnapStat label="Spent" value={credits.spent ?? 0} accent="#dc3545" />
        <SnapStat label="Added" value={credits.added ?? 0} accent="#28a745" />
        <SnapStat label="Available" value={credits.available ?? 0} accent="#007bff" />
      </SnapSection>

      <SnapSection title="Entities Created">
        <SnapStat label="Agents" value={entities.agents ?? 0} accent="#6366f1" />
        <SnapStat label="Team Leads" value={entities.team_leads ?? 0} accent="#8b5cf6" />
        <SnapStat label="Call Centers" value={entities.call_centres ?? 0} accent="#0ea5e9" />
        <SnapStat label="Nested Companies" value={entities.nested_companies ?? 0} accent="#f59e0b" />
      </SnapSection>

      {daily.length > 0 && (
        <div className="mb-3">
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Daily Calls
          </div>
          <div className={SNAP_PANEL + ' max-h-[190px] overflow-auto p-0'}>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="px-2.5 py-1.5 text-left">Day</th>
                  <th className="px-2.5 py-1.5 text-right">In</th>
                  <th className="px-2.5 py-1.5 text-right">Out</th>
                  <th className="px-2.5 py-1.5 text-right">Answered</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((d, i) => (
                  <tr key={d.day || i} className="border-t border-border">
                    <td className="px-2.5 py-1.5">{d.day || '—'}</td>
                    <td className="px-2.5 py-1.5 text-right">{d.incoming ?? 0}</td>
                    <td className="px-2.5 py-1.5 text-right">{d.outgoing ?? 0}</td>
                    <td className="px-2.5 py-1.5 text-right">{d.answered ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <button
        type="button"
        className="text-xs text-primary hover:underline"
        onClick={() => setShowRaw((v) => !v)}
      >
        {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
      </button>
      {showRaw && (
        <pre className={SNAP_PANEL + ' mt-2 mb-0 max-h-[240px] overflow-auto text-xs'}>
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

/* ────────────────────────── licence table ────────────────────────── */
const LicenceTable = ({ licence }) => {
  const available = licence?.available_licence || {};
  const used = licence?.used_licence || {};
  if (typeof available !== 'object' || typeof used !== 'object') {
    return (
      <div className="py-3 text-center text-sm text-muted-foreground">
        No licence information available.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="w-1/2 px-3 py-2 text-left font-medium">Type</th>
            <th className="w-1/4 px-3 py-2 text-center font-medium">Available</th>
            <th className="w-1/4 px-3 py-2 text-center font-medium">Used</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(LICENCE_LABEL).map((key) => (
            <tr key={key} className="border-t border-border">
              <td className="px-3 py-2">{LICENCE_LABEL[key] || key}</td>
              <td className="px-3 py-2 text-center font-semibold">{available?.[key] ?? 0}</td>
              <td className="px-3 py-2 text-center">{used?.[key] ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ────────────────────────── ACL parsing ────────────────────────── */
const parseAclModuleOperations = (aclData) => {
  try {
    if (!aclData?.module_operations) return null;
    if (typeof aclData.module_operations === 'string') {
      try {
        return parseModuleOperations(aclData.module_operations);
      } catch {
        return null;
      }
    }
    if (typeof aclData.module_operations === 'object') return aclData.module_operations;
    return null;
  } catch {
    return null;
  }
};

const summarizeAclModules = (moduleOperations) => {
  if (
    !moduleOperations ||
    typeof moduleOperations !== 'object' ||
    Object.keys(moduleOperations).length === 0
  ) {
    return [];
  }
  return Object.entries(moduleOperations)
    .filter(([, ops]) => {
      if (!ops || typeof ops !== 'object') return false;
      return Object.values(ops).some((val) => val === true);
    })
    .map(([module, ops]) => {
      const p = ops || {};
      const enabled = [];
      if (p.read || p.Read || p.READ) enabled.push({ type: 'Read', tone: 'success' });
      if (p.add || p.Add || p.ADD) enabled.push({ type: 'Add', tone: 'primary' });
      if (p.edit || p.Edit || p.EDIT) enabled.push({ type: 'Edit', tone: 'warning' });
      if (p.delete || p.Delete || p.DELETE) enabled.push({ type: 'Delete', tone: 'destructive' });
      if (p.view_permission || p.View_Permission || p.VIEW_PERMISSION || p.viewPermission || p.ViewPermission)
        enabled.push({ type: 'View Permission', tone: 'info' });
      return { module, permissions: enabled, options: getAclModuleOptionLabels(module, p) };
    });
};

const aclToneClass = (tone) => {
  switch (tone) {
    case 'success':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'primary':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
    case 'warning':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'destructive':
      return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
    case 'info':
      return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200';
    default:
      return 'bg-muted text-foreground';
  }
};

/* ═════════════════════════ main page ═════════════════════════ */
const UserProfilePage = ({ onBack } = {}) => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [voicePlanNumbers, setVoicePlanNumbers] = useState([]);
  const [loadingVoicePlanNumbers, setLoadingVoicePlanNumbers] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showACLModal, setShowACLModal] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [showLocationsModal, setShowLocationsModal] = useState(false);
  const [showCallCenterModal, setShowCallCenterModal] = useState(false);
  const [showVoicePlanModal, setShowVoicePlanModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const FONT_OPTIONS = [
    { label: 'Poppins', value: "'Poppins', sans-serif" },
    { label: 'Montserrat', value: "'Montserrat', sans-serif" },
    { label: 'Inter', value: "'Inter', sans-serif" },
    { label: 'JetBrains Mono', value: "'JetBrains Mono', monospace" },
    { label: 'Workbench', value: "'Workbench', sans-serif" },
    { label: 'Bebas Neue', value: "'Bebas Neue', sans-serif" },
    { label: 'Playfair Display', value: "'Playfair Display', serif" },
    { label: 'Playwrite England SemiJoined', value: "'Playwrite England SemiJoined', cursive" },
  ];

  const [bodyFont, setBodyFont] = useState(localStorage.getItem('bodyFont') || "'Poppins', sans-serif");
  const [headingFont, setHeadingFont] = useState(
    localStorage.getItem('headingFont') || "'Montserrat', sans-serif",
  );

  const SCALE_OPTIONS = [50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120];
  const getDefaultDisplayScale = () => {
    const host = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    return INTERNAL_HOSTS.includes(host) ? 100 : 75;
  };
  const [displayScale, setDisplayScale] = useState(() => {
    const s = sessionStorage.getItem('displayScale');
    const n = s ? parseInt(s, 10) : NaN;
    return Number.isFinite(n) ? n : getDefaultDisplayScale();
  });

  const PET_OPTIONS = [
    'default', 'ace', 'black', 'bunny', 'calico', 'eevee', 'esmeralda',
    'fox', 'ghost', 'gray', 'jess', 'kina', 'lucy', 'maia', 'maria',
    'mike', 'silver', 'silversky', 'snuupy', 'spirit', 'tora', 'valentine',
  ];
  const BEHAVIOR_OPTIONS = [
    { value: 'follow', label: 'Follow mouse' },
    { value: 'wander', label: 'Wander around' },
  ];
  const [curiosityEnabled, setCuriosityEnabled] = useState(
    sessionStorage.getItem('curiosityEnabled') === 'true',
  );
  const [selectedPet, setSelectedPet] = useState(sessionStorage.getItem('curiosityPet') || 'default');
  const [petCount, setPetCount] = useState(Number(sessionStorage.getItem('curiosityPetCount')) || 1);
  const [curiosityBehavior, setCuriosityBehavior] = useState(
    sessionStorage.getItem('curiosityBehavior') || 'follow',
  );
  const [curiositySize, setCuriositySize] = useState(Number(sessionStorage.getItem('curiositySize')) || 32);
  const [curiositySpeed, setCuriositySpeed] = useState(Number(sessionStorage.getItem('curiositySpeed')) || 10);

  const [loadingCompanyModal, setLoadingCompanyModal] = useState(false);
  const [loadingCallCenterModal, setLoadingCallCenterModal] = useState(false);
  const [loadingACLModal, setLoadingACLModal] = useState(false);
  const [demoUsage, setDemoUsage] = useState({ loading: false, ttsQuota: null, summary: null });
  const [showDemoSnapshot, setShowDemoSnapshot] = useState(false);
  const [showAssetsVisibility, setShowAssetsVisibility] = useState(false);

  const assetsVisibility = useAssetsVisibilityEligibility();

  /* ── logout — kept verbatim from v1 ── */
  const handleLogout = async () => {
    if (isLoggingOut) return;
    if (isExitBlocked()) {
      toast.error(`${getExitBlockReason()} Please finish before logging out.`);
      return;
    }
    try {
      const user = getUser();
      if (!user || !user.id) {
        toast.error('User not found');
        return;
      }
      setIsLoggingOut(true);
      trackAppLogout({
        profile_type: (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('activeLoginType')) || null,
        from: 'profile_page',
      });

      if (user.role === USER_ROLES.CALL_CENTRE_AGENT) {
        try { socket.disconnect(); } catch { /* noop */ }
      }
      // Reseller/company/call-centre-admin roles connect adminSocket, not
      // the agent socket above — this bespoke logout (kept separate from
      // useLogoutMutation for the impersonation switch-back flow below)
      // previously never disconnected it. Unconditional + no-op-safe: a
      // socket that was never connected ignores disconnect() harmlessly.
      try { adminSocket.disconnect(); } catch { /* noop */ }
      try {
        if (typeof window !== 'undefined' && typeof window.__ctiReleaseAllMedia === 'function') {
          window.__ctiReleaseAllMedia();
        }
      } catch { /* noop */ }

      let userStack = Storage.getItem('userStack');
      userStack = userStack ? JSON.parse(userStack) : [];

      await postApiCall(`${API_USER_LOGOUT}/${user.id}`);

      Storage.multiRemove([
        'showPhone', 'showHistory', 'showForm',
        'positionPhone', 'positionHistory', 'positionForm',
        'loginTimestamp', 'idleTimestamp',
      ]);

      if (userStack.length > 0) {
        const switchedBack = await switchBackUser();
        if (switchedBack) {
          window.location.href = `/${ROUTES.DASHBOARD}`;
        } else {
          console.log('Failed to switch back user');
        }
      } else {
        await logoutUser();
        clearUser();
        Storage.removeItem('userStack');
        window.location.href = `/${ROUTES.LOGIN}`;
      }
    } catch (error) {
      const msg = typeof error === 'string' ? error : error?.message || '';
      if (msg.includes('TokenExpiredError')) {
        Storage.clearAll();
        setTimeout(() => window.location.replace(`/${ROUTES.LOGIN}`), 1000);
        toast.error('Session Expired');
      } else {
        toast.error(msg || 'Logout failed');
      }
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    checkAuth();
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNumberVoicePlans = async (userId: any) => {
    try {
      setLoadingVoicePlanNumbers(true);
      const resp = await getApiCall(
        API_USER_NUMBER_VOICE_PLANS.replace(':id', userId),
        {},
      );
      setVoicePlanNumbers(
        Array.isArray(resp?.number_voice_plans) ? resp.number_voice_plans : [],
      );
    } catch (err) {
      console.warn('Unable to load allocated numbers:', err);
      setVoicePlanNumbers([]);
    } finally {
      setLoadingVoicePlanNumbers(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const user = getUser();
      if (!user || !user.id) {
        toast.error('User not found');
        setLoading(false);
        return;
      }
      try {
        const profileApiUrl = API_USERS_PROFILE.replace(':id', user.id);
        const resp = await getApiCall(profileApiUrl, {});
        setUserData(resp);
        if (Array.isArray(resp?.number_voice_plans)) {
          setVoicePlanNumbers(resp.number_voice_plans);
        }
        fetchNumberVoicePlans(user.id);
      } catch (apiError) {
        console.warn('API call failed, using basic user data from storage:', apiError);
        setUserData({
          id: user.id,
          username: user.username || 'N/A',
          name: user.name || 'N/A',
          email: user.email || 'N/A',
          role: user.role,
          status: user.status,
          expiry_date: user.expiry_date,
          credits_available: user.credits_available,
          tts_credits: user.tts_credits,
          company: user.company,
          assignedCompany: user.assignedCompany,
        });
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      try {
        const user = getUser();
        if (user) {
          setUserData({
            id: user.id,
            username: user.username || 'N/A',
            name: user.name || 'N/A',
            email: user.email || 'N/A',
            role: user.role,
            status: user.status,
            expiry_date: user.expiry_date,
            credits_available: user.credits_available,
            tts_credits: user.tts_credits,
            company: user.company,
            assignedCompany: user.assignedCompany,
          });
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        toast.error('Failed to load user profile');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--body-font', bodyFont);
    document.documentElement.style.setProperty('--heading-font', headingFont);
    localStorage.setItem('bodyFont', bodyFont);
    localStorage.setItem('headingFont', headingFont);
  }, [bodyFont, headingFont]);

  useEffect(() => {
    document.body.style.zoom = '';
    document.documentElement.style.zoom = `${displayScale}%`;
    document.documentElement.style.setProperty('--app-zoom', String(displayScale / 100));
    sessionStorage.setItem('displayScale', String(displayScale));
  }, [displayScale]);

  useEffect(() => {
    sessionStorage.setItem('curiosityEnabled', curiosityEnabled);
    sessionStorage.setItem('curiosityPet', selectedPet);
    sessionStorage.setItem('curiosityPetCount', petCount);
    sessionStorage.setItem('curiosityBehavior', curiosityBehavior);
    sessionStorage.setItem('curiositySize', curiositySize);
    sessionStorage.setItem('curiositySpeed', curiositySpeed);
    window.dispatchEvent(new Event('curiosity:update'));
  }, [curiosityEnabled, selectedPet, petCount, curiosityBehavior, curiositySize, curiositySpeed]);

  const curiosityFirstRunRef = useRef(true);
  useEffect(() => {
    if (curiosityFirstRunRef.current) {
      curiosityFirstRunRef.current = false;
      return;
    }
    savePanelCustomization({
      curiosity: {
        enabled: curiosityEnabled,
        pet: selectedPet,
        count: petCount,
        behavior: curiosityBehavior,
        size: curiositySize,
        speed: curiositySpeed,
      },
    });
  }, [curiosityEnabled, selectedPet, petCount, curiosityBehavior, curiositySize, curiositySpeed]);

  const fetchDemoUsage = async () => {
    const company =
      userData?.assigned_company_details || userData?.assignedCompany || userData?.company;
    if (company?.is_demo_company !== true) {
      setDemoUsage({ loading: false, ttsQuota: null, summary: null });
      return;
    }
    setDemoUsage((prev) => ({ ...prev, loading: true }));

    let moduleOps = {};
    try {
      const raw = JSON.parse(Storage.getItem('user') || '{}');
      const ops = raw?.acl_settings?.module_operations;
      moduleOps = typeof ops === 'string' ? JSON.parse(ops) : ops || {};
    } catch { moduleOps = {}; }
    const canReadCompanyList =
      userData?.role === USER_ROLES.SUPERADMIN ||
      hasPermission(moduleOps, MODULE_ACL.COMPANY, 'read');

    const [ttsQuota, summary] = await Promise.all([
      getApiCall(API_TTS_DEMO_QUOTA, {}).catch((err) => {
        console.error('Failed to load demo TTS quota:', err);
        return null;
      }),
      canReadCompanyList && company?.id
        ? getApiCall(API_COMPANY_DEMO_SUMMARY.replace(':id', company.id), {}).catch((err) => {
            console.error('Failed to load demo company summary:', err);
            return null;
          })
        : Promise.resolve(null),
    ]);

    setDemoUsage({ loading: false, ttsQuota: ttsQuota || null, summary: summary || null });
  };

  const openCompanyModal = () => {
    setLoadingCompanyModal(true);
    setShowCompanyModal(true);
    fetchDemoUsage();
    setTimeout(() => setLoadingCompanyModal(false), 600);
  };
  const openCallCenterModal = () => {
    setLoadingCallCenterModal(true);
    setShowCallCenterModal(true);
    setTimeout(() => setLoadingCallCenterModal(false), 600);
  };
  const openACLModal = () => {
    setLoadingACLModal(true);
    setShowACLModal(true);
    setTimeout(() => setLoadingACLModal(false), 600);
  };

  const refreshCallCenter = async () => {
    const cc = userData?.assigned_call_center_details || userData?.assignedCallCenter;
    if (!cc?.id) { toast.error('Call center id missing'); return; }
    setLoadingCallCenterModal(true);
    try {
      const url = API_CALL_CENTRE_PROFILE.replace(':id', cc.id);
      const resp = await getApiCall(url, {});
      setUserData((prev) => {
        const next = { ...prev };
        if (prev.assigned_call_center_details) next.assigned_call_center_details = resp;
        if (prev.assignedCallCenter) next.assignedCallCenter = resp;
        return next;
      });
    } catch (err) {
      console.error('Failed to refresh call center:', err);
      toast.error('Failed to refresh call center');
    } finally {
      setLoadingCallCenterModal(false);
    }
  };

  const refreshCompany = async () => {
    const company = userData?.assigned_company_details || userData?.assignedCompany || userData?.company;
    if (!company?.id) { toast.error('Company id missing'); return; }
    setLoadingCompanyModal(true);
    try {
      const url = API_COMPANY_PROFILE.replace(':id', company.id);
      const resp = await getApiCall(url, {});
      setUserData((prev) => {
        const next = { ...prev };
        if (prev.assigned_company_details) next.assigned_company_details = resp;
        if (prev.assignedCompany) next.assignedCompany = resp;
        if (prev.company && !prev.assigned_company_details && !prev.assignedCompany) next.company = resp;
        return next;
      });
    } catch (err) {
      console.error('Failed to refresh company:', err);
      toast.error('Failed to refresh company');
    } finally {
      setLoadingCompanyModal(false);
      if (company?.is_demo_company === true) fetchDemoUsage();
    }
  };

  if (loading) return <Loader />;

  if (!userData) {
    return (
      <div className="p-4">
        <Card>
          <CardHeader>
            <CardTitle>User Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">Unable to load user profile data.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getRoleLabel = () => {
    const role = userData.role;
    if (role === USER_ROLES.COMPANY_ADMIN) return 'Company';
    if (role === USER_ROLES.CALL_CENTRE_ADMIN || role === USER_ROLES.CALL_CENTRE_TEAM_LEAD)
      return 'Call Center';
    if (role === USER_ROLES.CALL_CENTRE_AGENT) return 'Agent';
    return USER_ROLES_MAP[role] || role;
  };
  const userRoleLabel = getRoleLabel();
  const expiryDate = userData.expiry_date ? new Date(userData.expiry_date).toLocaleDateString() : 'N/A';
  const joinedDate =
    userData.joined_date || userData.created_at
      ? new Date(userData.joined_date || userData.created_at).toLocaleDateString()
      : 'N/A';
  const companyData = userData.assigned_company_details || userData.assignedCompany || userData.company;
  const callCenterData = userData.assigned_call_center_details || userData.assignedCallCenter;
  const aclData = userData.acl_details || userData.acl_settings;
  const companyGroups = userData.company_groups || [];
  const companyLocations = userData.company_locations || [];
  const userGroups = userData.user_groups || [];
  const userLocations = userData.user_locations || [];
  const showVoicePlansSection =
    loadingVoicePlanNumbers ||
    voicePlanNumbers.length > 0 ||
    userData.role === USER_ROLES.RESELLER ||
    userData.role === USER_ROLES.COMPANY_ADMIN;
  const showAvailableLicenceSection =
    userData.role !== USER_ROLES.SUPERADMIN && userData.role !== USER_ROLES.CALL_CENTRE_AGENT;

  const getLicenceFromEntity = (entity) => {
    if (!entity) return null;
    return entity.sub_user_licence || entity.subUserLicenceId || entity.sub_user_licence_id || null;
  };

  const agentDetails = userData.agent_details;
  const hasAgent = !!agentDetails;

  const isNonCompanyCallCentreRole = [
    USER_ROLES.CALL_CENTRE_ADMIN,
    USER_ROLES.CALL_CENTRE_TEAM_LEAD,
    USER_ROLES.CALL_CENTRE_AGENT,
  ].includes(userData.role);

  const accountStatus = (() => {
    if (!userData.account_expiry_date) return null;
    const exp = new Date(userData.account_expiry_date);
    const today = new Date();
    exp.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const isExpired = exp < today;
    const daysUntilExpiry = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    return { isExpired, daysUntilExpiry };
  })();

  const isSuperAdmin = userData.role === USER_ROLES.SUPERADMIN;

  const selectCls =
    'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

  return (
    <ScrollPage>
      <PageHeader
        title="User Profile"
        description="Account, assignments and preferences."
        actions={
          <Button variant="outline" size="sm" onClick={onBack || (() => navigate(-1))} className="gap-1">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />
      <div className="mx-auto max-w-5xl space-y-4">

        {/* Identity */}
        <SectionCard title="Identity">
          <MetaGrid cols={3}>
            <MetaTile icon={FiUser} label="Username">{userData.username || 'N/A'}</MetaTile>
            <MetaTile icon={FiUser} label="Name">{userData.name || 'N/A'}</MetaTile>
            <MetaTile icon={FiMail} label="Email">{userData.email || 'N/A'}</MetaTile>
            <MetaTile icon={FiShield} label="Role">{userRoleLabel}</MetaTile>
            <MetaTile icon={FiCalendar} label="Expiry Date">{expiryDate}</MetaTile>
            <MetaTile icon={FiClock} label="Joined Date">{joinedDate}</MetaTile>
            {hasAgent && (
              <MetaTile icon={FiPhone} label="Number">
                {agentDetails.agent_number || 'N/A'}
              </MetaTile>
            )}
            {userData.parent_username && (
              <MetaTile icon={FiUsers} label="Parent">
                {userData.parent_username}
                {userData.parent_name && ` (${userData.parent_name})`}
              </MetaTile>
            )}
            {userData.parent_parent_username && (
              <MetaTile icon={FiUsers} label="Parent's Parent">
                {userData.parent_parent_username}
                {userData.parent_parent_name && ` (${userData.parent_parent_name})`}
              </MetaTile>
            )}
          </MetaGrid>
        </SectionCard>

        {/* Account status */}
        {accountStatus && (
          <SectionCard title="Account Status">
            <div className="flex flex-wrap items-center gap-2">
              {accountStatus.isExpired ? (
                <Badge variant="destructive" className="gap-1">
                  <FiXCircle size={12} /> Expired
                </Badge>
              ) : (
                <Badge className="gap-1 bg-emerald-600 text-white hover:bg-emerald-600">
                  <FiCheckCircle size={12} /> Active
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">
                {accountStatus.daysUntilExpiry > 0
                  ? `${accountStatus.daysUntilExpiry} days remaining`
                  : accountStatus.daysUntilExpiry === 0
                    ? 'Expires today'
                    : `${Math.abs(accountStatus.daysUntilExpiry)} days expired`}
              </span>
            </div>
          </SectionCard>
        )}

        {/* Assignments */}
        {(companyData || callCenterData || aclData || showVoicePlansSection) && (
          <SectionCard title="Assignments">
            <MetaGrid>
              {companyData && !isNonCompanyCallCentreRole && (
                <MetaTile icon={FiBriefcase} label="Assigned Company">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{companyData.name || 'N/A'}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto gap-1 p-0 text-primary"
                      onClick={openCompanyModal}
                    >
                      <Info className="h-3.5 w-3.5" /> Details
                    </Button>
                  </div>
                </MetaTile>
              )}
              {callCenterData && (
                <>
                  <MetaTile icon={FiHome} label="Assigned Call Center">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{callCenterData.name || 'N/A'}</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto gap-1 p-0 text-primary"
                        onClick={openCallCenterModal}
                      >
                        <Info className="h-3.5 w-3.5" /> Details
                      </Button>
                    </div>
                  </MetaTile>
                  <MetaTile icon={Info} label="Primary PBX">
                    {callCenterData.primaryPbx?.dialer_name || 'N/A'}
                  </MetaTile>
                  <MetaTile icon={Info} label="Secondary PBX">
                    {callCenterData.secondaryPbx?.dialer_name || '—'}
                  </MetaTile>
                  {callCenterData.flow_type !== undefined && callCenterData.flow_type !== null && (
                    <MetaTile icon={Info} label="Flow Type">
                      <Badge variant={callCenterData.flow_type === 1 ? 'default' : 'secondary'}>
                        {callCenterData.flow_type === 1 ? 'Jambonz' : 'Asterisk'}
                      </Badge>
                    </MetaTile>
                  )}
                </>
              )}
              {aclData && (
                <MetaTile icon={FiKey} label="Assigned ACL">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{aclData.acl_name || 'N/A'}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto gap-1 p-0 text-primary"
                      onClick={openACLModal}
                    >
                      <Info className="h-3.5 w-3.5" /> Details
                    </Button>
                  </div>
                </MetaTile>
              )}
              {showVoicePlansSection && (
                <MetaTile icon={FiPhone} label="Numbers Allocated">
                  <div className="flex flex-wrap items-center gap-2">
                    {loadingVoicePlanNumbers ? (
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading numbers…
                      </span>
                    ) : voicePlanNumbers.length > 0 ? (
                      <>
                        <span>{voicePlanNumbers.length} Number(s) linked</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto gap-1 p-0 text-primary"
                          onClick={() => setShowVoicePlanModal(true)}
                        >
                          <Info className="h-3.5 w-3.5" /> Details
                        </Button>
                      </>
                    ) : (
                      <span className="text-muted-foreground">No numbers allocated.</span>
                    )}
                  </div>
                </MetaTile>
              )}
              {(companyGroups.length > 0 || userGroups.length > 0) && (
                <MetaTile icon={FiLayers} label="Dialer Groups">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto gap-1 p-0 text-primary"
                      onClick={() => setShowGroupsModal(true)}
                    >
                      <Info className="h-3.5 w-3.5" /> View all ({companyGroups.length + userGroups.length})
                    </Button>
                    {companyGroups.slice(0, 3).map((group, idx) => (
                      <Badge key={idx} variant="secondary">
                        {group.group_name || `Group ${group.group_id}`}
                      </Badge>
                    ))}
                    {companyGroups.length > 3 && (
                      <Badge variant="outline">+{companyGroups.length - 3} more</Badge>
                    )}
                  </div>
                </MetaTile>
              )}
              {(companyLocations.length > 0 || userLocations.length > 0) && (
                <MetaTile icon={FiMapPin} label="Dialer Locations">
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto gap-1 p-0 text-primary"
                    onClick={() => setShowLocationsModal(true)}
                  >
                    <Info className="h-3.5 w-3.5" /> View all ({companyLocations.length + userLocations.length})
                  </Button>
                </MetaTile>
              )}
            </MetaGrid>
          </SectionCard>
        )}

        {/* Agent details */}
        {hasAgent && (
          <SectionCard title="Agent Details">
            <MetaGrid cols={3}>
              {agentDetails.extension_name && (
                <MetaTile icon={FiUser} label="Extension Name">{agentDetails.extension_name}</MetaTile>
              )}
              {agentDetails.cli_option && (
                <MetaTile icon={FiUser} label="Number Shown on Calls">
                  {agentDetails.cli_option === 'actual' ? 'Actual Customer Number' : 'Virtual Number'}
                </MetaTile>
              )}
              <MetaTile icon={FiPhone} label="Outbound Calling">
                <Badge variant={agentDetails.is_outbound_enabled ? 'default' : 'secondary'}>
                  {agentDetails.is_outbound_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </MetaTile>
              {agentDetails.cli_option && (
                <MetaTile icon={FiShield} label="Number Masking">
                  <Badge variant={agentDetails.is_masking_enabled ? 'default' : 'secondary'}>
                    {agentDetails.is_masking_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </MetaTile>
              )}
              {/* {agentDetails.default_start_screen && (
                <MetaTile icon={Info} label="Default Start Screen">
                  {agentDetails.default_start_screen === 'call-stats' ? 'Call Stats' : 'Default'}
                </MetaTile>
              )} */}
              {agentDetails.first_call_acceptance && (
                <MetaTile icon={Info} label="First Call Acceptance">
                  <span className="capitalize">{agentDetails.first_call_acceptance}</span>
                </MetaTile>
              )}
              <MetaTile icon={FiPhone} label="Voicemail">
                <Badge variant={agentDetails.is_voicemail_enabled ? 'default' : 'secondary'}>
                  {agentDetails.is_voicemail_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </MetaTile>
              {agentDetails.access && (
                <MetaTile icon={FiShield} label="Access">
                  <Badge className="bg-sky-600 text-white hover:bg-sky-600">{agentDetails.access}</Badge>
                </MetaTile>
              )}
              {Array.isArray(agentDetails.type) && agentDetails.type.length > 0 && (
                <MetaTile icon={Info} label="Type">
                  <div className="flex flex-wrap gap-1">
                    {agentDetails.type.map((t, idx) => (
                      <Badge
                        key={idx}
                        className="bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                </MetaTile>
              )}
            </MetaGrid>
          </SectionCard>
        )}

        {/* Preferences */}
        {/* <SectionCard title="Preferences">
          <div className="space-y-4"> */}
            {/* <div className="flex flex-wrap items-center gap-3">
              <label className="flex min-w-[160px] items-center gap-2 text-sm font-medium">
                <Columns3 className="h-4 w-4 opacity-70" /> Sidebar Position
              </label>
              <SidebarPositionToggle />
            </div> */}

            {/* {isSuperAdmin && (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex min-w-[160px] items-center gap-2 text-sm font-medium">
                    <MdFontDownload className="h-4 w-4 opacity-70" /> Body Font
                  </label>
                  <select
                    className={selectCls + ' max-w-[260px]'}
                    value={bodyFont}
                    onChange={(e) => setBodyFont(e.target.value)}
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                  <Button variant="outline" size="sm" onClick={() => setBodyFont("'Poppins', sans-serif")}>
                    Reset
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex min-w-[160px] items-center gap-2 text-sm font-medium">
                    <TypeIcon className="h-4 w-4 opacity-70" /> Heading Font
                  </label>
                  <select
                    className={selectCls + ' max-w-[260px]'}
                    value={headingFont}
                    onChange={(e) => setHeadingFont(e.target.value)}
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                  <Button variant="outline" size="sm" onClick={() => setHeadingFont("'Montserrat', sans-serif")}>
                    Reset
                  </Button>
                </div>
              </>
            )} */}

            {/* <div className="flex flex-wrap items-center gap-3">
              <label className="flex min-w-[160px] items-center gap-2 text-sm font-medium">
                <FiLayers className="h-4 w-4 opacity-70" /> Display Scale
              </label>
              <select
                className={selectCls + ' max-w-[260px]'}
                value={displayScale}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setDisplayScale(n);
                  savePanelCustomization({ display_scale: n });
                }}
              >
                {SCALE_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}%</option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const n = getDefaultDisplayScale();
                  setDisplayScale(n);
                  savePanelCustomization({ display_scale: n });
                }}
              >
                Reset
              </Button>
            </div> */}

            {/* <div className="space-y-3">
              <label className="flex items-center gap-3 text-sm font-medium">
                <FiHeart className="h-4 w-4 opacity-70" />
                <span className="min-w-[130px]">Curiosity</span>
                <input
                  type="checkbox"
                  checked={curiosityEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setCuriosityEnabled(enabled);
                    if (!enabled) {
                      setSelectedPet('default');
                      setPetCount(1);
                    }
                  }}
                  className="h-4 w-4"
                />
                <span className="text-muted-foreground">Enable Curiosity</span>
              </label>
              {curiosityEnabled && (
                <div className="ml-8 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className={selectCls + ' max-w-[220px]'}
                      value={selectedPet}
                      onChange={(e) => setSelectedPet(e.target.value)}
                    >
                      {PET_OPTIONS.map((pet) => (
                        <option key={pet} value={pet}>{pet}</option>
                      ))}
                    </select>
                    <Button variant="outline" size="sm" onClick={() => setSelectedPet('default')}>Reset</Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className={selectCls + ' max-w-[220px]'}
                      value={petCount}
                      onChange={(e) => setPetCount(Number(e.target.value))}
                    >
                      <option value={1}>1 Character</option>
                      <option value={2}>2 Characters</option>
                      <option value={3}>3 Characters</option>
                    </select>
                    <Button variant="outline" size="sm" onClick={() => setPetCount(1)}>Reset</Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="min-w-[90px] text-sm">Behavior</span>
                    <select
                      className={selectCls + ' max-w-[220px]'}
                      value={curiosityBehavior}
                      onChange={(e) => setCuriosityBehavior(e.target.value)}
                    >
                      {BEHAVIOR_OPTIONS.map((b) => (
                        <option key={b.value} value={b.value}>{b.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="min-w-[90px] text-sm">Size: {curiositySize}px</span>
                    <input
                      type="range"
                      min={16}
                      max={96}
                      step={4}
                      value={curiositySize}
                      onChange={(e) => setCuriositySize(Number(e.target.value))}
                      className="max-w-[220px]"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="min-w-[90px] text-sm">Speed: {curiositySpeed}</span>
                    <input
                      type="range"
                      min={2}
                      max={40}
                      step={1}
                      value={curiositySpeed}
                      onChange={(e) => setCuriositySpeed(Number(e.target.value))}
                      className="max-w-[220px]"
                    />
                  </div>
                </div>
              )}
            </div> */}
          {/* </div>
        </SectionCard> */}

        {assetsVisibility.canChangeOwnAssetsVisibility && (
          <SectionCard title="Assets Visibility">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Control whether your assigned company's assets can be
                private or public.
              </p>
              <Button variant="outline" onClick={() => setShowAssetsVisibility(true)}>
                Manage
              </Button>
            </div>
          </SectionCard>
        )}

        <AssetsVisibilityModal
          open={showAssetsVisibility}
          onOpenChange={setShowAssetsVisibility}
          company={
            assetsVisibility.assignedCompany
              ? {
                  id: assetsVisibility.assignedCompany.id,
                  name: userData?.assignedCompany?.name ?? null,
                }
              : null
          }
        />

        <div className="flex justify-end pt-2">
          <Button
            variant="destructive"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="gap-2"
          >
            {isLoggingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
          </Button>
        </div>
      </div>

      {/* ═════════════ Company Dialog ═════════════ */}
      {companyData && (
        <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Company Details</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              {loadingCompanyModal ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {showAvailableLicenceSection && (
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <h6 className="text-sm font-bold">Licence</h6>
                        <button
                          type="button"
                          onClick={refreshCompany}
                          title="Refresh company"
                          disabled={loadingCompanyModal}
                          className="text-primary transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <RefreshCw
                            size={16}
                            className={loadingCompanyModal ? 'animate-spin' : ''}
                          />
                        </button>
                      </div>
                      <LicenceTable licence={getLicenceFromEntity(companyData)} />
                    </div>
                  )}
                  <MetaGrid cols={2}>
                    <MetaTile icon={FiBriefcase} label="Company Name">{companyData.name || 'N/A'}</MetaTile>
                    <MetaTile icon={FiPhone} label="Company Phone">{companyData.phone || 'N/A'}</MetaTile>
                    <MetaTile icon={FiMap} label="Company Address">{companyData.address || 'N/A'}</MetaTile>
                    {companyData.pincode && <MetaTile icon={FiMapPin} label="Pincode">{companyData.pincode}</MetaTile>}
                    {companyData.credits_available !== undefined && (
                      <MetaTile label="Company Voice Credits">
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">
                          {companyData.credits_available || '0.00'}
                        </span>
                      </MetaTile>
                    )}
                    {companyData.tts_credits !== undefined && (
                      <MetaTile label="Company TTS Credits">
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">
                          {companyData.tts_credits || '0.00'}
                        </span>
                      </MetaTile>
                    )}
                    {companyData.is_demo_company !== undefined && (
                      <MetaTile label="Is Demo Company ?">
                        <span className="font-bold">{companyData.is_demo_company ? 'Yes' : 'No'}</span>
                      </MetaTile>
                    )}
                    {companyData.is_demo_company === true && (
                      <>
                        <MetaTile icon={FiActivity} label="Voice Credits Consumed">
                          <span className="font-bold">
                            {demoUsage.loading
                              ? 'Loading…'
                              : demoUsage.summary?.credits?.spent !== undefined &&
                                  demoUsage.summary?.credits?.spent !== null
                                ? demoUsage.summary.credits.spent
                                : 'N/A'}
                          </span>
                        </MetaTile>
                        <MetaTile icon={FiCreditCard} label="Recharge Count">
                          <span className="font-bold">
                            {demoUsage.summary?.overview?.recharge_count ??
                              companyData.demo_recharge_count ??
                              0}
                          </span>
                        </MetaTile>
                        <MetaTile icon={FiType} label="TTS Cap Remaining (Today)">
                          <span className="font-bold">
                            {demoUsage.loading
                              ? 'Loading…'
                              : demoUsage.ttsQuota?.isDemo
                                ? `${demoUsage.ttsQuota.remaining} of ${demoUsage.ttsQuota.limit} left (used ${demoUsage.ttsQuota.used})`
                                : 'N/A'}
                          </span>
                        </MetaTile>
                      </>
                    )}
                    {companyData.demo_converted_at && (
                      <MetaTile icon={FiCheckCircle} label="Demo Converted At">
                        <span className="font-bold">{formatDate(companyData.demo_converted_at)}</span>
                      </MetaTile>
                    )}
                    <MetaTile icon={FiType} label="Company TTS Plan">
                      {companyData.assignedTtsPlan
                        ? `${companyData.assignedTtsPlan.plan_name} (${companyData.assignedTtsPlan.per_character}/char, ${companyData.assignedTtsPlan.per_variable}/var, ${companyData.assignedTtsPlan.pulse_price}/pulse, ${companyData.assignedTtsPlan.unit_balance === 'paise' ? 'Paise' : 'Rupees'})`
                        : 'N/A'}
                    </MetaTile>
                    {companyData.is_reseller_company !== undefined && (
                      <MetaTile icon={Info} label="Reseller Company">
                        <Badge variant={companyData.is_reseller_company ? 'default' : 'secondary'}>
                          {companyData.is_reseller_company ? 'Yes' : 'No'}
                        </Badge>
                      </MetaTile>
                    )}
                    {companyData.is_own_company !== undefined && (
                      <MetaTile icon={Info} label="End User">
                        <Badge variant={companyData.is_own_company ? 'default' : 'secondary'}>
                          {companyData.is_own_company ? 'Yes' : 'No'}
                        </Badge>
                      </MetaTile>
                    )}
                    {companyData.created_at && (
                      <MetaTile icon={FiClock} label="Created At">
                        {formatDate(companyData.created_at)}
                      </MetaTile>
                    )}
                  </MetaGrid>
                  {companyData.demo_summary_snapshot && (
                    <div className="rounded-md border border-border p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <FiLayers size={14} className="opacity-70" /> Demo Summary Snapshot
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDemoSnapshot((v) => !v)}
                        >
                          {showDemoSnapshot ? 'Hide snapshot' : 'View snapshot'}
                        </Button>
                      </div>
                      {showDemoSnapshot && <DemoSnapshotView snapshot={companyData.demo_summary_snapshot} />}
                    </div>
                  )}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ═════════════ ACL Dialog ═════════════ */}
      {aclData && (
        <Dialog open={showACLModal} onOpenChange={setShowACLModal}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Your Assigned Permissions</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              {loadingACLModal ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
                </div>
              ) : (
                <>
                  <div className="mb-3 flex items-center gap-2 text-sm">
                    <FiKey size={16} className="opacity-70" />
                    <span className="font-bold">ACL Name:</span>
                    <span>{aclData.acl_name || 'N/A'}</span>
                  </div>
                  {(() => {
                    const moduleOperations = parseAclModuleOperations(aclData);
                    const enabledModules = summarizeAclModules(moduleOperations);

                    if (enabledModules.length === 0) {
                      return (
                        <div className="py-6 text-center">
                          <Info className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">No permissions assigned to this ACL</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            This ACL does not have any enabled permissions configured.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div>
                        <div className="mb-2 flex items-center gap-2 text-sm">
                          <FiLayers size={14} className="opacity-70" />
                          <span className="font-bold">Your Assigned Permissions:</span>
                          <Badge className="bg-sky-600 text-white hover:bg-sky-600">
                            {enabledModules.length} Module{enabledModules.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <div className="max-h-[450px] overflow-y-auto rounded-md border border-border">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-muted/60">
                              <tr>
                                <th className="w-[28%] px-3 py-2 text-left font-semibold">Module</th>
                                <th className="w-[40%] px-3 py-2 text-left font-semibold">Assigned Permissions</th>
                                <th className="w-[32%] px-3 py-2 text-left font-semibold">Additional Options</th>
                              </tr>
                            </thead>
                            <tbody>
                              {enabledModules.map((item, idx) => (
                                <tr key={idx} className="border-t border-border">
                                  <td className="px-3 py-2 align-middle">
                                    <div className="flex items-center gap-2">
                                      <FiLayers className="text-sky-500" size={14} />
                                      <span>{item.module}</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 align-middle">
                                    <div className="flex flex-wrap gap-1.5">
                                      {item.permissions.map((perm, permIdx) => (
                                        <span
                                          key={permIdx}
                                          className={
                                            'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ' +
                                            aclToneClass(perm.tone)
                                          }
                                        >
                                          <FiCheckCircle size={11} />
                                          {perm.type}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 align-middle">
                                    {item.options.length === 0 ? (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    ) : (
                                      <div className="flex flex-wrap gap-1.5">
                                        {item.options.map((option) => (
                                          <span
                                            key={option}
                                            className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
                                          >
                                            <FiCheckCircle size={11} />
                                            {option}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ═════════════ Call Center Dialog ═════════════ */}
      {callCenterData && (
        <Dialog open={showCallCenterModal} onOpenChange={setShowCallCenterModal}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Call Center Details</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              {loadingCallCenterModal ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
                </div>
              ) : (
                <>
                  {showAvailableLicenceSection && (
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <h6 className="text-sm font-bold">Licence</h6>
                        <button
                          type="button"
                          onClick={refreshCallCenter}
                          title="Refresh call center"
                          disabled={loadingCallCenterModal}
                          className="text-primary transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <RefreshCw
                            size={16}
                            className={loadingCallCenterModal ? 'animate-spin' : ''}
                          />
                        </button>
                      </div>
                      <LicenceTable licence={getLicenceFromEntity(callCenterData)} />
                    </div>
                  )}
                  <MetaGrid cols={2}>
                    <MetaTile icon={FiHome} label="Call Center Name">
                      {callCenterData.name || 'N/A'}
                    </MetaTile>
                    {callCenterData.domain && (
                      <MetaTile icon={Info} label="Domain">{callCenterData.domain}</MetaTile>
                    )}
                    {callCenterData.flow_type !== undefined && callCenterData.flow_type !== null && (
                      <MetaTile icon={Info} label="Flow Type">
                        <Badge variant={callCenterData.flow_type === 1 ? 'default' : 'secondary'}>
                          {callCenterData.flow_type === 1 ? 'Jambonz' : 'Asterisk'}
                        </Badge>
                      </MetaTile>
                    )}
                    <MetaTile icon={Info} label="Primary PBX">
                      {callCenterData.primaryPbx?.dialer_name || 'N/A'}
                    </MetaTile>
                    <MetaTile icon={Info} label="Secondary PBX">
                      {callCenterData.secondaryPbx?.dialer_name || '—'}
                    </MetaTile>
                    {callCenterData.created_at && (
                      <MetaTile icon={FiCalendar} label="Created At">
                        {formatDate(callCenterData.created_at)}
                      </MetaTile>
                    )}
                  </MetaGrid>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ═════════════ Voice Plan Dialog ═════════════ */}
      {voicePlanNumbers.length > 0 && (
        <Dialog open={showVoicePlanModal} onOpenChange={setShowVoicePlanModal}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>Numbers Allocated</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] overflow-y-auto">
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Number</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Voice Plan</th>
                      <th className="px-3 py-2 text-left font-medium">Plan Details (Duration / Price)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voicePlanNumbers.map((item, idx) => (
                      <tr key={item.id || idx} className="border-t border-border">
                        <td className="px-3 py-2">{item.actual_number || 'N/A'}</td>
                        <td className="px-3 py-2">{item.number_type || 'N/A'}</td>
                        <td className="px-3 py-2">{item.voice_plan?.plan_name || 'Not Assigned'}</td>
                        <td className="px-3 py-2">
                          {flattenPlansToArray(item.voice_plan?.plans).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {flattenPlansToArray(item.voice_plan.plans).map((plan, pIdx) => (
                                <Badge
                                  key={pIdx}
                                  className="bg-sky-100 text-sky-900 hover:bg-sky-100 dark:bg-sky-900/40 dark:text-sky-200"
                                >
                                  {getVoicePlanLabel(plan)}: {plan.pulse_duration ?? 'N/A'} /{' '}
                                  {plan.pulse_price ?? 'N/A'}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ═════════════ Groups Dialog ═════════════ */}
      {(companyGroups.length > 0 || userGroups.length > 0) && (
        <Dialog open={showGroupsModal} onOpenChange={setShowGroupsModal}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Dialer Groups Information</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
              {companyGroups.length > 0 && (
                <div>
                  <h5 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <FiLayers size={14} /> Company Dialer Groups ({companyGroups.length})
                  </h5>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Dialer Group Name</th>
                          <th className="px-3 py-2 text-left font-medium">Dialer Group ID</th>
                          <th className="px-3 py-2 text-left font-medium">Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyGroups.map((group, idx) => (
                          <tr key={idx} className="border-t border-border">
                            <td className="px-3 py-2">{group.group_name || `Group ${group.group_id}`}</td>
                            <td className="px-3 py-2">{group.group_id}</td>
                            <td className="px-3 py-2">{formatDate(group.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {userGroups.length > 0 && (
                <div>
                  <h5 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <FiUsers size={14} /> User Dialer Groups ({userGroups.length})
                  </h5>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Dialer Group ID</th>
                          <th className="px-3 py-2 text-left font-medium">Company ID</th>
                          <th className="px-3 py-2 text-left font-medium">Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userGroups.map((group, idx) => (
                          <tr key={idx} className="border-t border-border">
                            <td className="px-3 py-2">{group.group_id}</td>
                            <td className="px-3 py-2">{group.company_id}</td>
                            <td className="px-3 py-2">{formatDate(group.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ═════════════ Locations Dialog ═════════════ */}
      {(companyLocations.length > 0 || userLocations.length > 0) && (
        <Dialog open={showLocationsModal} onOpenChange={setShowLocationsModal}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Dialer Locations Information</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
              {companyLocations.length > 0 && (
                <div>
                  <h5 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <FiMapPin size={14} /> Company Dialer Locations ({companyLocations.length})
                  </h5>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Dialer Location Name</th>
                          <th className="px-3 py-2 text-left font-medium">Dialer Location ID</th>
                          <th className="px-3 py-2 text-left font-medium">Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyLocations.map((location, idx) => (
                          <tr key={idx} className="border-t border-border">
                            <td className="px-3 py-2">
                              {location.location_name || `Location ${location.location_id}`}
                            </td>
                            <td className="px-3 py-2">{location.location_id}</td>
                            <td className="px-3 py-2">{formatDate(location.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {userLocations.length > 0 && (
                <div>
                  <h5 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <FiUsers size={14} /> User Dialer Locations ({userLocations.length})
                  </h5>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Dialer Location ID</th>
                          <th className="px-3 py-2 text-left font-medium">Company ID</th>
                          <th className="px-3 py-2 text-left font-medium">Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userLocations.map((location, idx) => (
                          <tr key={idx} className="border-t border-border">
                            <td className="px-3 py-2">{location.location_id}</td>
                            <td className="px-3 py-2">{location.company_id}</td>
                            <td className="px-3 py-2">{formatDate(location.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </ScrollPage>
  );
};

export default UserProfilePage;
